import React, { useState, useRef } from 'react'
import { Box, Typography } from '@mui/material'
import { L, Icon, PortalMenu, itemSx, useLavMobile } from '../ui/LavDropdown'

// LavDropdown and its shared primitives now live in src/components/ui/LavDropdown.
// Re-exported here so existing importers of this module keep working.
export { L, Icon, LavDropdown, LavMobileContext, useLavMobile } from '../ui/LavDropdown'

/**
 * appointmentLavenderFields — lavender-themed form controls recreated from the
 * Claude Design "Add Appointment Flow" (Step 1). These are scoped to the Add
 * Appointment flow only (not the shared AppointmentFormFields) so other screens
 * are unaffected. Exposes: design tokens (L), Icon, LavSectionTitle, LavChip /
 * LavChipRow, LavDropdown, LavAddonField, RepeatControl, plus the recurrence
 * helpers used by both the control and its parent.
 */

// ---- section title (ck-sec-title) ----
export function LavSectionTitle({ children, hint }) {
	const mobile = useLavMobile()
	return (
		<Box sx={{
			display: 'flex', alignItems: 'center', gap: '6px', mb: mobile ? '9px' : '8px',
			fontSize: mobile ? '12px' : '11.5px', fontWeight: 700, textTransform: 'uppercase',
			letterSpacing: '0.08em', color: L.primaryDeep, fontFamily: L.font,
		}}>
			<span>{children}</span>
			{hint && (
				<Box component="em" sx={{
					fontStyle: 'normal', fontWeight: 600, fontSize: '10.5px', color: L.muted,
					letterSpacing: '0.04em', textTransform: 'lowercase',
					'&::before': { content: '"· "' },
				}}>
					{hint}
				</Box>
			)}
		</Box>
	)
}

// ---- chips (ck-chip / ck-chips) ----
export function LavChipRow({ children }) {
	return <Box sx={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>{children}</Box>
}

export function LavChip({ on, onClick, top, white }) {
	const mobile = useLavMobile()
	return (
		<Box
			component="button"
			type="button"
			onClick={onClick}
			sx={{
				flex: '1 1 0', minWidth: 0, borderRadius: mobile ? '11px' : L.rSm, p: mobile ? '10px 4px' : '8px 4px',
				display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '1px',
				border: `1.5px solid ${on ? L.primary : L.border}`,
				bgcolor: on ? L.primarySoft : (white ? '#FFFFFF' : L.field),
				cursor: 'pointer', fontFamily: L.font,
				transition: 'border-color .12s, background .12s',
				'&:hover': { borderColor: L.primary },
			}}
		>
			<Typography sx={{
				fontWeight: 700, fontSize: mobile ? '13px' : '12.5px', whiteSpace: 'nowrap', fontFamily: L.font,
				color: on ? L.primaryDeep : L.text,
			}}>
				{top}
			</Typography>
		</Box>
	)
}

// ---- custom numeric input (ck-custom-input) ----
export function LavCustomInput({ value, onChange, suffix, min, max, step = 1, white }) {
	const mobile = useLavMobile()
	return (
		<Box sx={{
			mt: '8px', display: 'flex', alignItems: 'center', gap: '6px',
			border: `1.5px solid ${L.border}`, borderRadius: mobile ? '12px' : L.rSm,
			px: '12px', height: mobile ? '46px' : '38px',
			bgcolor: white ? '#FFFFFF' : L.field,
			'&:focus-within': { borderColor: L.primary, boxShadow: `0 0 0 3px ${L.primarySoft}` },
		}}>
			<Box
				component="input"
				type="number"
				value={value}
				min={min}
				max={max}
				step={step}
				onChange={onChange}
				sx={{
					border: 'none', outline: 'none', width: mobile ? '72px' : '64px', font: 'inherit',
					fontFamily: L.font, fontSize: mobile ? '14.5px' : '13.5px', fontWeight: 650,
					bgcolor: 'transparent', color: L.text,
				}}
			/>
			<Typography sx={{ fontSize: mobile ? '13.5px' : '12.5px', color: L.muted, fontWeight: 600, fontFamily: L.font }}>
				{suffix}
			</Typography>
		</Box>
	)
}

// ---- add-ons multi-select with chips (ap-chipfield) ----
// addOns: full list [{ id, name, price, duration_minutes }]
// selected: array of selected addon objects; onToggle(id)
export function LavAddonField({ addOns, selected, onToggle, formatCurrency, disabled = false }) {
	const mobile = useLavMobile()
	const [open, setOpen] = useState(false)
	const triggerRef = useRef(null)
	const selectedIds = selected.map((a) => a.id)

	return (
		<Box sx={{ position: 'relative', fontFamily: L.font }}>
			<Box
				ref={triggerRef}
				role="button"
				tabIndex={0}
				onClick={() => { if (!disabled) setOpen(!open) }}
				onKeyDown={(e) => { if (!disabled && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); setOpen(!open) } }}
				sx={{
					minHeight: mobile ? '50px' : '42px', border: `1.5px solid ${open ? L.primary : L.border}`,
					borderRadius: mobile ? '12px' : L.rSm,
					bgcolor: L.field, display: 'flex', flexWrap: 'wrap', gap: '6px', alignItems: 'center',
					p: mobile ? '8px 40px 8px 10px' : '6px 38px 6px 8px', position: 'relative',
					cursor: disabled ? 'default' : 'pointer',
					opacity: disabled ? 0.55 : 1,
					boxShadow: open ? `0 0 0 3px ${L.primarySoft}` : 'none',
					'&:hover': { borderColor: disabled ? L.border : L.primary },
				}}
			>
				{!selected.length ? (
					<Box component="span" sx={{ color: L.muted, fontSize: mobile ? '15px' : '13.5px', pl: '4px' }}>
						Select add-ons (optional)…
					</Box>
				) : null}
				{selected.map((a) => (
					<Box key={a.id} sx={{
						display: 'flex', alignItems: 'center', gap: '5px', bgcolor: L.primarySoft,
						color: L.primaryDeep, fontSize: mobile ? '13px' : '12px', fontWeight: 650,
						p: mobile ? '6px 6px 6px 10px' : '4px 5px 4px 9px', borderRadius: mobile ? '9px' : '8px',
					}}>
						{a.name} – {formatCurrency(a.price)}
						<Box
							component="button"
							type="button"
							aria-label={'Remove ' + a.name}
							onClick={(e) => { e.stopPropagation(); onToggle(a.id) }}
							sx={{
								border: 'none', bgcolor: 'transparent', color: 'inherit', display: 'flex',
								p: '2px', borderRadius: '50%', cursor: 'pointer',
								'&:hover': { bgcolor: 'rgba(95, 78, 194, 0.16)' },
							}}
						>
							<Icon name="x" size={11} />
						</Box>
					</Box>
				))}
				<Box component="span" sx={{
					position: 'absolute', right: '12px', top: '50%', display: 'flex', color: L.muted,
					transform: open ? 'translateY(-50%) rotate(180deg)' : 'translateY(-50%)',
					transition: 'transform .15s',
				}}>
					<Icon name="chevron" size={14} />
				</Box>
			</Box>

			<PortalMenu anchorRef={triggerRef} open={open} onClose={() => setOpen(false)}>
				{addOns.map((a) => {
					const on = selectedIds.includes(a.id)
					return (
						<Box
							component="button"
							type="button"
							key={a.id}
							role="option"
							aria-selected={on}
							onClick={() => onToggle(a.id)}
							sx={{
								...itemSx,
								...(mobile ? { minHeight: '48px', borderRadius: '11px', fontSize: '14.5px', p: '9px 12px' } : {}),
								...(on ? { bgcolor: L.primarySoft } : {}),
							}}
						>
							<Box sx={{
								width: mobile ? '22px' : '18px', height: mobile ? '22px' : '18px',
								borderRadius: mobile ? '7px' : '6px', flex: 'none',
								display: 'flex', alignItems: 'center', justifyContent: 'center',
								border: `1.5px solid ${on ? L.primary : L.border}`,
								bgcolor: on ? L.primary : '#FFFFFF', color: '#FFFFFF',
							}}>
								{on ? <Icon name="tick" size={mobile ? 13 : 11} sw={2.6} /> : null}
							</Box>
							<Box component="span" sx={{ flex: 1, textAlign: 'left', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
								{a.name}
							</Box>
							<Box component="span" sx={{ fontSize: mobile ? '13px' : '12px', color: L.muted, fontWeight: 600, flex: 'none' }}>
								{formatCurrency(a.price)} · {a.duration_minutes || 0}m
							</Box>
						</Box>
					)
				})}
				{!addOns.length && (
					<Box sx={{ p: '20px 10px', textAlign: 'center', color: L.muted, fontSize: '12.5px' }}>
						No add-ons available
					</Box>
				)}
			</PortalMenu>
		</Box>
	)
}

// ============================================================================
// Recurrence helpers — shared by RepeatControl (preview) and the parent
// (occurrence generation). Mirrors the design's apOccurrences/apFreqLabel.
// ============================================================================

// Hard cap on the number of appointments a single recurring series can create.
export const AP_OCC_CAP = 40

export const REPEAT_DEFAULT = { on: false, freq: 4, customWeeks: '6', ends: 'count', endDate: '', count: '12' }

function parseDate(s) {
	const [y, m, d] = (s || '').split('-').map(Number)
	return new Date(y || 2026, (m || 1) - 1, d || 1)
}
function dateToStr(d) {
	return d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0') + '-' + String(d.getDate()).padStart(2, '0')
}
function fmtDay(d) {
	return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
}

// Date of the Nth (1-based) occurrence in the series, given the frequency.
function occurrenceDate(first, repeat, index) {
	const d = new Date(first)
	if (repeat.freq === 'monthly') d.setMonth(first.getMonth() + index)
	else {
		const wk = repeat.freq === 'custom' ? Math.max(1, parseInt(repeat.customWeeks, 10) || 1) : repeat.freq
		d.setDate(first.getDate() + 7 * wk * index)
	}
	return d
}

/**
 * Latest valid end date — the date of the AP_OCC_CAP-th occurrence — so the
 * "On date" picker can't request more than the cap allows. Returns YYYY-MM-DD.
 */
export function maxEndDateStr(dateStr, repeat) {
	if (!dateStr) return undefined
	return dateToStr(occurrenceDate(parseDate(dateStr), repeat, AP_OCC_CAP - 1))
}

/**
 * Generate the recurrence occurrence dates (Date objects) for a start date and
 * repeat config. Always bounded by AP_OCC_CAP. Returns { dates, openEnded }.
 */
export function apOccurrences(dateStr, repeat) {
	const first = parseDate(dateStr)
	const out = [first]
	if (!repeat || !repeat.on) return { dates: out, openEnded: false }
	const endDate = repeat.ends === 'date' && repeat.endDate ? parseDate(repeat.endDate) : null
	const target = repeat.ends === 'count'
		? Math.min(AP_OCC_CAP, Math.max(1, parseInt(repeat.count, 10) || 1))
		: AP_OCC_CAP
	let i = 1
	while (out.length < target) {
		const d = occurrenceDate(first, repeat, i)
		if (endDate && d > endDate) break
		out.push(d)
		i++
	}
	return { dates: out, openEnded: false }
}

export function apFreqLabel(repeat) {
	if (repeat.freq === 1) return 'week'
	if (repeat.freq === 2) return '2 weeks'
	if (repeat.freq === 4) return '4 weeks'
	if (repeat.freq === 'monthly') return 'month'
	const n = Math.max(1, parseInt(repeat.customWeeks, 10) || 1)
	return n === 1 ? 'week' : n + ' weeks'
}

function apPreviewText(dateStr, repeat) {
	const { dates, openEnded } = apOccurrences(dateStr, repeat)
	const shown = dates.slice(0, 4).map(fmtDay).join(' · ')
	if (openEnded) return shown + ' · ongoing'
	const extra = dates.length - 4
	return extra > 0 ? shown + ' · +' + extra + ' more' : shown
}

// ---- recurring schedule control (step 1, "repeats on") ----
export function RepeatControl({ repeat, setRepeat, dateStr, EndDatePicker }) {
	const mobile = useLavMobile()
	const set = (p) => setRepeat({ ...repeat, ...p })
	const FREQ = [
		{ v: 1, l: 'Weekly' }, { v: 2, l: '2 wks' }, { v: 4, l: '4 wks' },
		{ v: 'monthly', l: 'Monthly' }, { v: 'custom', l: 'Custom' },
	]
	return (
		<Box sx={{
			bgcolor: L.surface, border: `1px solid ${L.border}`,
			borderRadius: mobile ? '15px' : L.r, p: mobile ? '13px' : '12px', fontFamily: L.font,
		}}>
			<Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
				<Box sx={{
					width: mobile ? '34px' : '30px', height: mobile ? '34px' : '30px', flex: 'none',
					borderRadius: mobile ? '11px' : '10px', bgcolor: L.primarySoft,
					color: L.primaryDeep, display: 'flex', alignItems: 'center', justifyContent: 'center',
				}}>
					<Icon name="repeat" size={15} />
				</Box>
				<Box sx={{ flex: 1, minWidth: 0 }}>
					<Typography sx={{ fontSize: mobile ? '14.5px' : '13.5px', fontWeight: 700, color: L.text, fontFamily: L.font }}>
						Repeat this appointment
					</Typography>
					<Typography sx={{ fontSize: mobile ? '12px' : '11.5px', color: L.muted, mt: '1px', fontFamily: L.font }}>
						Applies to every pet on this appointment
					</Typography>
				</Box>
				<Box
					component="button"
					type="button"
					role="switch"
					aria-checked={repeat.on}
					onClick={() => set({ on: !repeat.on })}
					sx={{
						width: mobile ? '46px' : '40px', height: mobile ? '28px' : '24px', flex: 'none',
						borderRadius: '99px', border: 'none', p: 0,
						position: 'relative', cursor: 'pointer', transition: 'background .15s',
						bgcolor: repeat.on ? L.primary : L.border,
						'&::after': {
							content: '""', position: 'absolute', top: '3px',
							left: repeat.on ? (mobile ? '21px' : '19px') : '3px',
							width: mobile ? '22px' : '18px', height: mobile ? '22px' : '18px',
							borderRadius: '50%', bgcolor: '#FFFFFF',
							transition: 'left .15s', boxShadow: '0 1px 3px rgba(46, 42, 64, 0.25)',
						},
					}}
				/>
			</Box>

			{repeat.on && (
				<Box sx={{ mt: '13px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
					<Box>
						<Typography sx={repeatLabelSx}>Every</Typography>
						<LavChipRow>
							{FREQ.map((f) => (
								<LavChip key={String(f.v)} top={f.l} white on={repeat.freq === f.v} onClick={() => set({ freq: f.v })} />
							))}
						</LavChipRow>
						{repeat.freq === 'custom' && (
							<LavCustomInput
								white
								value={repeat.customWeeks}
								min={1}
								max={52}
								suffix="weeks apart"
								onChange={(e) => set({ customWeeks: e.target.value })}
							/>
						)}
					</Box>

					<Box>
						<Typography sx={repeatLabelSx}>Ends</Typography>
						<LavChipRow>
							<LavChip top="On date" white on={repeat.ends === 'date'} onClick={() => set({ ends: 'date' })} />
							<LavChip top="After…" white on={repeat.ends === 'count'} onClick={() => set({ ends: 'count' })} />
						</LavChipRow>
						{repeat.ends === 'date' && EndDatePicker && (
							<Box sx={{ mt: '8px' }}>
								{EndDatePicker({
									value: repeat.endDate,
									onChange: (v) => set({ endDate: v }),
									min: dateStr,
									max: maxEndDateStr(dateStr, repeat),
									placeholder: 'Pick an end date',
								})}
							</Box>
						)}
						{repeat.ends === 'count' && (
							<LavCustomInput
								white
								value={repeat.count}
								min={1}
								max={AP_OCC_CAP}
								suffix={`visits total (max ${AP_OCC_CAP})`}
								onChange={(e) => set({ count: e.target.value })}
							/>
						)}
					</Box>

					<Box sx={{
						fontSize: mobile ? '12.5px' : '12px', color: L.muted, display: 'flex', gap: '6px', alignItems: 'center',
						fontWeight: 600, fontFamily: L.font,
						'& svg': { color: L.primary, flex: 'none' },
					}}>
						<Icon name="calendar" size={13} />
						<span>{apPreviewText(dateStr, repeat)}</span>
					</Box>
				</Box>
			)}
		</Box>
	)
}

const repeatLabelSx = {
	fontSize: '10.5px', fontWeight: 750, letterSpacing: '0.07em', textTransform: 'uppercase',
	color: L.muted, mb: '6px', fontFamily: L.font,
}

